import { ClockCircleOutlined, InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Popover, Tooltip, Input, Button, Upload, Dropdown, Menu } from 'antd';
import { TextAreaRef } from 'antd/es/input/TextArea';
import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { ToolbarIcon } from '../../ToolbarIcon';
import HistoryVideList from './HistoryVideoList';
import useHistoryVideoList from './hooks/useHistoryVideoList';
import useUploadVideo from './hooks/useUploadVideo';
import { useChatConversationContext } from 'views/Conversation/Chat/contexts/ChatConversationContext';
import { supportSendVideoPlatforms } from 'services/config/platform';
import { uploadClass } from '../styles';

const PopoverTitle = styled.div`
  height: 40px;
  color: #515359;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 428px;
  .ant-input-search {
    width: 200px;
  }
`;
const PopoverContent = styled.div`
  width: 428px;
  .footer {
    height: 48px;
    width: calc(100% + 32px);
    position: relative;
    left: -16px;
    bottom: -12px;
    line-height: 48px;
    padding-left: 10px;
    background: rgba(240, 242, 245, 1);
    border-radius: 0 0 2px 2px;
    box-shadow: 0 -2px 8px 0 rgba(167, 178, 204, 0.1), inset 0 1px 0 0 rgba(240, 242, 245, 1);
  }
`;

const supportHistoryPlatforms: Conversation.Conversation['channel'][] = ['SHOPEE'];

export interface VideoUploadProps {
  textInputRef: React.RefObject<TextAreaRef>;
  targetLang: General.Language;
  onMercadoChatView?: boolean;
}

const VideoUploadCore = (props: VideoUploadProps & { convInfo: Conversation.Conversation }) => {
  const { textInputRef, targetLang, onMercadoChatView, convInfo } = props;

  const [showType, setShowType] = useState<'history' | 'upload' | null>(null);
  const { t } = useTranslate();
  const { onBeforeUpload, uploadProps } = useUploadVideo(
    textInputRef,
    targetLang,
    onMercadoChatView,
  );
  const { channel, storeId } = convInfo;

  const visible = showType === 'history';

  const { list, loadMore, onSearch, loading, hasMore } = useHistoryVideoList(visible, storeId);

  if (!supportHistoryPlatforms.includes(channel)) {
    return (
      <Upload className={uploadClass} beforeUpload={onBeforeUpload} {...uploadProps}>
        <Tooltip title={t('scene.chatWorkspace.messageEditor.upload.action.uploadVideo')}>
          <ToolbarIcon icon="video" />
        </Tooltip>
      </Upload>
    );
  }

  return (
    <Popover
      trigger={['click']}
      placement="top"
      visible={visible}
      onVisibleChange={(visible) => {
        if (!visible) {
          setShowType(null);
        }
      }}
      title={
        <PopoverTitle>
          <div>
            {t('scene.chatWorkspace.messageEditor.upload.history.title')}
            <Tooltip title={t('scene.chatWorkspace.messageEditor.upload.history.description')}>
              <InfoCircleOutlined />
            </Tooltip>
          </div>
          <Input.Search
            placeholder={t('scene.chatWorkspace.messageEditor.upload.search.placeholder')}
            onSearch={onSearch}
          />
        </PopoverTitle>
      }
      content={
        <PopoverContent>
          <HistoryVideList loadMore={loadMore} list={list} loading={loading} hasMore={hasMore} />
          <div className="footer">
            <Upload beforeUpload={onBeforeUpload} {...uploadProps}>
              <Button icon={<UploadOutlined />}>
                {t('scene.chatWorkspace.messageEditor.upload.action.localUpload')}
              </Button>
            </Upload>
          </div>
        </PopoverContent>
      }
    >
      <Upload className={uploadClass} beforeUpload={onBeforeUpload} {...uploadProps}>
        <Dropdown
          placement="topLeft"
          overlay={
            <Menu>
              <Menu.Item
                key="historyUpload"
                icon={<ClockCircleOutlined />}
                onClick={(e) => {
                  setShowType('history');
                  e.domEvent.stopPropagation();
                }}
              >
                {t('scene.chatWorkspace.messageEditor.upload.action.historySend')}
              </Menu.Item>
              <Menu.Item key="localUpload" icon={<UploadOutlined />}>
                {t('scene.chatWorkspace.messageEditor.upload.action.localUpload')}
              </Menu.Item>
            </Menu>
          }
        >
          <ToolbarIcon icon="video" onClick={(e) => e.stopPropagation()} />
        </Dropdown>
      </Upload>
    </Popover>
  );
};

const VideoUpload = (props: VideoUploadProps) => {
  const { currentConversation: convInfo } = useChatConversationContext();

  if (!convInfo || !supportSendVideoPlatforms.includes(convInfo.channel)) {
    return null;
  }

  return <VideoUploadCore {...props} convInfo={convInfo} />;
};

export default VideoUpload;
